import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, supabaseConfigured } from './supabase'
import { initIAP, bindIAPUser, unbindIAPUser } from './api/iap'
import { registerPush, unregisterPush } from './push'
import type { ProfileRow } from './database.types'

interface AuthState {
  session: Session | null
  profile: ProfileRow | null
  loading: boolean
  /** true when VITE_SUPABASE_URL + ANON_KEY are set. */
  configured: boolean
  /**
   * Guest = no session, but the user has explicitly chosen to browse the
   * app without signing in. Persisted in localStorage so reloads don't
   * bounce them back to the auth screen.
   */
  isGuest: boolean

  // Actions
  /**
   * Sign in with either a username (no `@`) or an email. We translate
   * usernames to emails server-side via `lookup_email_by_username`,
   * then call signInWithPassword with the resolved email.
   */
  signInWithIdentifier: (usernameOrEmail: string, password: string) => Promise<void>
  /**
   * Sign up with username + email + password + display_name. After
   * the auth user is created we (a) call set_username so the chosen
   * handle is stored, and (b) update display_name on the profile.
   * If the username is taken we surface a clean error and the auth
   * user is rolled back via signOut so we don't strand orphans.
   */
  signUpWithUsername: (args: {
    username: string
    email: string
    password: string
    displayName: string
  }) => Promise<void>
  /** Triggers Supabase's recovery-email flow. */
  sendPasswordReset: (email: string) => Promise<void>
  /** Called from the recovery screen to set a new password. */
  updatePassword: (newPassword: string) => Promise<void>
  signInWithApple: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut:         () => Promise<void>
  enterAsGuest:    () => Promise<void>
  exitGuestMode:   () => void
  updateProfile:   (patch: Partial<Pick<ProfileRow, 'display_name' | 'avatar_url' | 'username' | 'language' | 'play_all_languages'>>) => Promise<void>
  /** True after Supabase fires PASSWORD_RECOVERY (email link clicked). */
  inPasswordRecovery: boolean

  /** Returns the providers currently linked to the active user. */
  linkedProviders: () => string[]
  /** Adds Apple as a sign-in method on the current account. Native flow on
   *  iOS, OAuth redirect on web. Supabase merges the identity into the
   *  current auth.users row when called from an authenticated session. */
  linkApple: () => Promise<void>
  /** Adds Google as a sign-in method on the current account. Same shape as
   *  linkApple: native Google sheet on iOS, OAuth redirect on web. */
  linkGoogle: () => Promise<void>
  /** Adds email/password as a sign-in method on the current account.
   *  Works for users who originally signed up via Apple and now want a
   *  fallback. Sets BOTH the email and the password in one call. */
  linkEmailPassword: (email: string, password: string) => Promise<void>
  /** Removes a provider from the account. Supabase blocks the call if it
   *  would leave the user with zero sign-in methods. */
  unlinkProvider: (provider: 'apple' | 'google' | 'email') => Promise<void>
}

/**
 * Translate Supabase's terse auth-link error messages into something a
 * non-technical user can act on. "Manual linking is disabled" comes from
 * the Supabase project's "Manual linking" setting being off; only the
 * project owner can flip that, so we point at it instead of just bouncing
 * the raw string up to the toast. That gate is on the OAuth link path;
 * the id-token path appears to enforce only a valid session, which is the
 * second family below.
 */
function friendlyLinkError(rawMessage: string): Error {
  const m = rawMessage.toLowerCase()
  if (m.includes('manual linking is disabled')) {
    return new Error(
      "Linking sign-in methods isn't turned on yet. Email talk@jordandraper.com so we can enable it.",
    )
  }
  // The refusal IS the protection, so the copy has to explain the state
  // rather than just report a failure. An identity belongs to exactly one
  // account, and accounts cannot be merged: trophies, Games Played, room
  // history and subscription all hang off one user row. Someone hitting
  // this already HAS a second account and needs to be told which one to
  // use, not told that something went wrong.
  if (m.includes('identity is already linked')) {
    return new Error(
      "You've already used that sign-in method on a different account. " +
      'Sign out and use it to sign in directly, or link a different one here.',
    )
  }
  // GoTrue's two session preconditions on an id-token LINK: it wants an
  // Authorization header and a target user it can resolve. Both mean the
  // session went stale between opening the screen and finishing the
  // provider sheet, which is a thing the user can actually fix.
  if (m.includes('linking requires a valid user')) {
    return new Error('Your session expired. Sign in again, then link this method.')
  }
  return friendlyIdTokenError(rawMessage)
}

/**
 * The two ways an ID-token sign-in fails on a CONFIG problem rather than a
 * user problem. Both are worth naming, because the raw strings are opaque
 * enough that you'd otherwise go hunting in the plugin.
 *
 * GoTrue rejects a bad audience with
 *   "Unacceptable audience in id_token: [<aud>]"
 * and helpfully prints the aud it actually saw (token_oidc.go, and it
 * passes if the token's audience matches ANY entry in the list, so adding
 * the second ID is additive). That means the raw text tells you exactly
 * which client ID is missing from Supabase's Authorized Client IDs, so we
 * keep it verbatim and only prepend the fix.
 *
 * "Nonces mismatch" means the hashing went wrong somewhere: Google gets
 * the SHA-256 hex digest, Supabase gets the raw string. See googleAuth.ts.
 */
function friendlyIdTokenError(rawMessage: string): Error {
  const m = rawMessage.toLowerCase()
  if (m.includes('unacceptable audience')) {
    return new Error(
      'That sign-in is not configured yet: Supabase is missing this ' +
      `app's Google client ID. Email talk@jordandraper.com with this: ${rawMessage}`,
    )
  }
  if (m.includes('nonce')) {
    return new Error(
      `Google sign-in failed a security check. Email talk@jordandraper.com with this: ${rawMessage}`,
    )
  }
  return new Error(rawMessage)
}

/**
 * Password-reset mail goes out through the branded pipeline as of
 * 2026-08-12: GoTrue calls our auth-email-hook Edge Function, which hands
 * delivery to the website's Resend templates. That hook is ALL-OR-NOTHING,
 * so a problem anywhere along it fails THIS API CALL rather than just
 * delaying an email, and GoTrue hands the hook's own error text straight
 * back to the client. Without this map the sign-in screen would print our
 * internals at the user: "website mailer refused (500): <!DOCTYPE html>".
 *
 * The copy's other job is to make a user who hits one tell us. Neither
 * side can see these: the app has no view of the hook, and Resend only
 * reports mail it actually sent, so a website deploy that starts
 * returning non-200 is invisible until somebody complains. Hence the
 * support address.
 *
 * ALLOWLIST, DELIBERATELY, and this is the whole point of the function.
 * The first version listed the known failure strings and passed anything
 * else through, which quietly assumed I could enumerate what a broken
 * pipeline says. I cannot: the text can come from GoTrue, from our Edge
 * Function, or from whatever the website's framework emits when it throws
 * somewhere unplanned, and that last one is exactly the case that
 * motivated this. A blocklist fails open, straight back to printing
 * internals at the user. So only the messages actually WORTH a user's
 * time get through, and everything else, known or not, becomes copy that
 * tells them what to do.
 */
function friendlyMailError(rawMessage: string): Error {
  const m = rawMessage.toLowerCase()

  // Not a failure, and already good copy: GoTrue's per-address throttle
  // states how long the user has to wait.
  if (m.includes('for security purposes') || m.includes('rate limit')) {
    return new Error(rawMessage)
  }

  // A typo'd address is the user's to fix, so say so rather than sending
  // them to support for it.
  if (m.includes('invalid format') || m.includes('validate email')) {
    return new Error(rawMessage)
  }

  // Everything else. An unknown address never reaches here: GoTrue
  // answers those with success so the screen cannot be used to confirm
  // who has an account.
  return new Error(
    "We couldn't send that email just now. Try again in a minute, and if it " +
    'keeps failing, email talk@jordandraper.com so we can look into it.',
  )
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<ProfileRow | null>(null)
  const [loading, setLoading] = useState(true)
  // Guest mode is per-session. We don't persist it to localStorage
  // so every cold load bounces unauth'd visitors through the auth
  // screen — they can re-tap "Continue as guest" if they want to
  // browse without an account.
  const [isGuest, setIsGuest] = useState<boolean>(false)
  // Set true when Supabase fires PASSWORD_RECOVERY (the user clicked
  // the reset email link). The router pivots into the
  // ResetPasswordScreen when this flag is on.
  const [inPasswordRecovery, setInPasswordRecovery] = useState(false)

  const configured = supabaseConfigured()

  // Bootstrap + subscribe to auth changes.
  useEffect(() => {
    if (!configured) { setLoading(false); return }

    let cancelled = false

    // Configure RevenueCat once at boot. No-op on web; on native it
    // talks to StoreKit / Play Billing on first call. We don't await
    // it — it runs in the background while the app boots, and any
    // subscribe() call awaits readiness internally.
    initIAP().catch(e => console.warn('[iap] init failed', e))

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setSession(data.session ?? null)
      setLoading(false)
      // If we already have a session at boot, tie it to RC so any
      // existing entitlement is recognised before the user reaches
      // a subscribe button. Register for push on the same beat (native
      // only; no-op on web).
      if (data.session?.user?.id) {
        bindIAPUser(data.session.user.id).catch(() => {})
        registerPush(data.session.user.id).catch(() => {})
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next ?? null)
      // PASSWORD_RECOVERY fires when the user clicks the link in the
      // reset email and lands back on the app. The Supabase client
      // creates a session for them so they can update their password.
      if (event === 'PASSWORD_RECOVERY') setInPasswordRecovery(true)
      if (event === 'SIGNED_OUT') {
        setInPasswordRecovery(false)
        // Drop the RC subscriber link so a shared device doesn't
        // leak entitlements to the next account on this phone.
        unbindIAPUser().catch(() => {})
      }
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && next?.user?.id) {
        bindIAPUser(next.user.id).catch(() => {})
        registerPush(next.user.id).catch(() => {})
      }
    })

    return () => { cancelled = true; subscription.unsubscribe() }
  }, [configured])

  // Load (or refresh) the profile whenever the session's user changes.
  // Also enforces the soft-delete gate: if `profiles.deleted_at` is
  // set, we sign the user out immediately so closed accounts can't
  // get back in via a cached session token. See the PrivacyScreen
  // soft-delete flow + migration 20260427140000.
  useEffect(() => {
    const userId = session?.user?.id
    if (!userId) { setProfile(null); return }

    let cancelled = false
    supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return
        if (error && error.code !== 'PGRST116') {
           
          console.error('[auth] failed to load profile', error)
        }
        if (data?.deleted_at) {
          // Closed account — kill the session and show the auth
          // screen. We don't surface a banner here; the redirect
          // itself is the signal, and the auth landing page is the
          // right place to start a fresh signup.
          unregisterPush(data.id)
            .finally(() => supabase.auth.signOut().catch(() => {}))
          setProfile(null)
          return
        }
        setProfile(data ?? null)
      })

    return () => { cancelled = true }
  }, [session?.user?.id])

  const value = useMemo<AuthState>(() => ({
    session,
    profile,
    loading,
    configured,
    isGuest,
    inPasswordRecovery,

    async enterAsGuest() {
      // Guests need a real Supabase session so they can call RLS-gated
      // RPCs (read the weekly event, vote, see winners). Sign them in
      // anonymously and flag the session as "guest" so the UI knows
      // not to offer them write-only features (submit a dish, etc).
      if (!configured) throw new Error('Supabase is not configured')
      const { data: { session: existing } } = await supabase.auth.getSession()
      if (!existing) {
        const { error } = await supabase.auth.signInAnonymously()
        if (error) throw error
      }
      setIsGuest(true)
    },

    exitGuestMode() {
      setIsGuest(false)
    },

    async signInWithIdentifier(usernameOrEmail: string, password: string) {
      if (!configured) throw new Error('Supabase is not configured')
      const id = usernameOrEmail.trim()
      if (id.includes('@')) {
        const { error } = await supabase.auth.signInWithPassword({
          email: id.toLowerCase(), password,
        })
        if (error) throw error
        return
      }
      // Username sign-in runs through an edge function that resolves the
      // email entirely server-side and returns only session tokens. The
      // old lookup_email_by_username RPC returned the raw email to any
      // caller — a mass email-harvest vector — and has been revoked.
      const { data, error } = await supabase.functions.invoke('login-with-username', {
        body: { username: id, password },
      })
      if (error) throw new Error('Wrong username or password.')
      const s = (data as {
        session?: { access_token: string; refresh_token: string }
      })?.session
      if (!s) throw new Error('Wrong username or password.')
      const { error: setErr } = await supabase.auth.setSession({
        access_token: s.access_token,
        refresh_token: s.refresh_token,
      })
      if (setErr) throw setErr
    },

    async signUpWithUsername({ username, email, password, displayName }) {
      if (!configured) throw new Error('Supabase is not configured')
      // Pre-flight: catch the obvious format/availability failures
      // before we create the auth user, so we don't strand orphans.
      const trimmedUser = username.trim().toLowerCase()
      const { data: ok, error: availErr } = await supabase.rpc('username_available', {
        p_username: trimmedUser,
      })
      if (availErr) throw availErr
      if (!ok) throw new Error('That username isn’t available. Try another.')

      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: { display_name: displayName.trim() || trimmedUser },
        },
      })
      if (error) throw error
      // We expect "Confirm email" to be OFF in Supabase — house rule
      // is no email confirmation codes / links for login. If a session
      // didn't come back, something is misconfigured upstream rather
      // than the user's problem; surface it as ours, not theirs.
      if (!data.session) {
        throw new Error(
          'Sign-up couldn’t finish. Please contact support. (Auth misconfigured: ' +
          'email confirmation must be disabled.)',
        )
      }
      // Now that we have a session, claim the username and write the
      // display_name. If either fails we sign out so the half-baked
      // account doesn't haunt them; they can retry cleanly.
      try {
        const { error: setErr } = await supabase.rpc('set_username', { p_username: trimmedUser })
        if (setErr) throw setErr
        const { error: nameErr } = await supabase
          .from('profiles')
          .update({ display_name: displayName.trim() || trimmedUser })
          .eq('id', data.session.user.id)
        if (nameErr) throw nameErr
      } catch (e) {
        await supabase.auth.signOut()
        throw e
      }
    },

    async sendPasswordReset(email: string) {
      if (!configured) throw new Error('Supabase is not configured')
      const { error } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: window.location.origin },
      )
      if (error) throw friendlyMailError(error.message)
    },

    async updatePassword(newPassword: string) {
      if (!configured) throw new Error('Supabase is not configured')
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setInPasswordRecovery(false)
    },

    async signInWithApple() {
      if (!configured) throw new Error('Supabase is not configured')

      // On iOS we use the native AuthenticationServices flow via
      // Capacitor — better UX (sheet, Face ID, no Safari hop) and it
      // works inside TestFlight/App Store builds where there is no
      // browser context for the web OAuth redirect. The native flow
      // returns Apple's identity-token JWT which Supabase accepts via
      // signInWithIdToken. Web stays on signInWithOAuth.
      const { Capacitor } = await import('@capacitor/core')
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        const { SignInWithApple } = await import('@capacitor-community/apple-sign-in')
        const res = await SignInWithApple.authorize({
          // clientId here is the iOS bundle ID, NOT the Services ID.
          // Apple verifies against the App ID's Sign In with Apple
          // capability — same Team ID gates both.
          clientId: 'com.jordandraper.games',
          redirectURI: 'https://kppibkvegzfvkiecxulu.supabase.co/auth/v1/callback',
          scopes: 'email name',
          state: crypto.randomUUID(),
        })
        const idToken = res.response?.identityToken
        if (!idToken) throw new Error('Apple did not return an identity token')
        const { error } = await supabase.auth.signInWithIdToken({
          provider: 'apple', token: idToken,
        })
        if (error) throw error
        return
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: { redirectTo: window.location.origin },
      })
      if (error) {
        const msg = (error.message || '').toLowerCase()
        if (msg.includes('not enabled') || msg.includes('provider')) {
          throw new Error('Apple Sign-In isn’t turned on yet. Try email or guest for now.')
        }
        throw error
      }
    },

    async signInWithGoogle() {
      if (!configured) throw new Error('Supabase is not configured')

      const { Capacitor } = await import('@capacitor/core')
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        const { getGoogleIdToken } = await import('./googleAuth')
        const { idToken, nonce } = await getGoogleIdToken()
        // The RAW nonce, not the digest we sent Google: Supabase hashes
        // this itself to compare against the token's claim. Omitted
        // entirely when the token has no claim, because GoTrue rejects
        // "nonce passed, none in token" as hard as it rejects a mismatch.
        const { error } = await supabase.auth.signInWithIdToken({
          provider: 'google', token: idToken, ...(nonce ? { nonce } : {}),
        })
        // Not a bare throw: a missing entry in Supabase's Authorized
        // Client IDs surfaces here as "Unacceptable audience in id_token",
        // which is unreadable to a player but names the exact ID we need
        // to add. friendlyIdTokenError keeps that detail and adds the fix.
        if (error) throw friendlyIdTokenError(error.message)
        return
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      })
      if (error) {
        const msg = (error.message || '').toLowerCase()
        if (msg.includes('not enabled') || msg.includes('provider')) {
          throw new Error('Google Sign-In isn’t turned on yet. Try email or guest for now.')
        }
        throw error
      }
    },

    async signOut() {
      if (!configured) return
      // Release this phone's push token BEFORE the session goes (the
      // delete needs the user's own RLS). Otherwise pushes for this
      // account keep arriving on the phone after someone else signs in.
      try {
        const { data } = await supabase.auth.getSession()
        if (data.session?.user?.id) await unregisterPush(data.session.user.id)
      } catch { /* best effort */ }
      // Google's SDK keeps its own cached account on the device and
      // Supabase's sign-out knows nothing about it, so without this the
      // next sign-in silently reuses the same Google account instead of
      // showing the picker. Best-effort and non-blocking.
      import('./googleAuth').then(m => m.googleSignOut()).catch(() => {})
      await supabase.auth.signOut()
      setSession(null)
      setProfile(null)
      // Belt-and-braces: clear the legacy localStorage flag that
      // earlier builds used to persist guest mode across reloads.
      // Any pre-existing value becomes inert once we ignore it.
      try { localStorage.removeItem('jdg.guestMode') } catch { /* ignore */ }
      setIsGuest(false)
    },

    async updateProfile(patch) {
      if (!session?.user?.id) throw new Error('Not signed in')
      const { data, error } = await supabase
        .from('profiles')
        .update(patch)
        .eq('id', session.user.id)
        .select()
        .single()
      if (error) throw error
      setProfile(data)
    },

    linkedProviders() {
      return (session?.user?.identities ?? [])
        .map(i => i.provider)
        .filter((p): p is string => Boolean(p))
    },

    async linkApple() {
      if (!configured) throw new Error('Supabase is not configured')
      if (!session?.user) throw new Error('Sign in first to link Apple')

      // Native iOS gets the Apple identity token the same way
      // signInWithApple does, but it must go to linkIdentity, NOT
      // signInWithIdToken. signInWithIdToken is a sign-IN: it switches
      // to whoever owns that Apple identity and MINTS A NEW USER when
      // nobody does. Only linkIdentity's id-token overload sends
      // link_identity: true alongside this session's JWT, which is what
      // tells GoTrue to attach the identity to the user who is already
      // signed in.
      //
      // Hide My Email is why this matters in practice: the identity's
      // address is @privaterelay.appleid.com, so it never matches the
      // account email and Supabase's automatic email-match linking
      // can't paper over the wrong call — you just get a duplicate
      // account. Testing with a matching email proves nothing, because
      // auto-linking then returns the right user for the wrong reason.
      //
      // The signed-out path in signInWithApple stays on
      // signInWithIdToken, because linkIdentity requires a session. That
      // guard at the top of this function is LOAD-BEARING now, not
      // defensive: the old buggy call worked without a session (by
      // silently making a new account), this one hard-fails.
      //
      // The web branch below needs "Manual linking" enabled in the auth
      // dashboard. It is NOT clear that the id-token branch does — GoTrue
      // enforces only an Authorization header and a resolvable target
      // user on that path, with no ManualLinking reference in
      // identity.go, token_oidc.go or external.go. Unverified either way,
      // so friendlyLinkError handles both error families.
      const { Capacitor } = await import('@capacitor/core')
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        const { SignInWithApple } = await import('@capacitor-community/apple-sign-in')
        const res = await SignInWithApple.authorize({
          clientId: 'com.jordandraper.games',
          redirectURI: 'https://kppibkvegzfvkiecxulu.supabase.co/auth/v1/callback',
          scopes: 'email name',
          state: crypto.randomUUID(),
        })
        const idToken = res.response?.identityToken
        if (!idToken) throw new Error('Apple did not return an identity token')
        // No nonce: we don't pass one to the plugin, so Apple's token
        // carries no nonce claim and Supabase has nothing to compare.
        const { error } = await supabase.auth.linkIdentity({
          provider: 'apple', token: idToken,
        })
        if (error) throw friendlyLinkError(error.message)
        // No manual refresh needed: linkIdentity saves the returned
        // session and fires USER_UPDATED, and our onAuthStateChange
        // handler setSession()s on every event, so linkedProviders()
        // sees the new identity on the next render.
        return
      }

      const { error } = await supabase.auth.linkIdentity({
        provider: 'apple',
        options: { redirectTo: window.location.origin },
      })
      if (error) throw friendlyLinkError(error.message)
    },

    async linkGoogle() {
      if (!configured) throw new Error('Supabase is not configured')
      if (!session?.user) throw new Error('Sign in first to link Google')

      // linkIdentity, not signInWithIdToken — see the long note in
      // linkApple above for why that distinction creates duplicate
      // accounts rather than an error.
      const { Capacitor } = await import('@capacitor/core')
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        const { getGoogleIdToken } = await import('./googleAuth')
        const { idToken, nonce } = await getGoogleIdToken()
        // Same nonce rule as signInWithGoogle: send it only if the token
        // actually carries the claim.
        const { error } = await supabase.auth.linkIdentity({
          provider: 'google', token: idToken, ...(nonce ? { nonce } : {}),
        })
        if (error) throw friendlyLinkError(error.message)
        return
      }

      const { error } = await supabase.auth.linkIdentity({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      })
      if (error) throw friendlyLinkError(error.message)
    },

    async linkEmailPassword(email: string, password: string) {
      if (!configured) throw new Error('Supabase is not configured')
      if (!session?.user) throw new Error('Sign in first to add email')
      const trimmed = email.trim().toLowerCase()
      if (!trimmed) throw new Error('Email required')
      if (password.length < 8) throw new Error('Password must be at least 8 characters')
      // updateUser with both email + password attaches the email
      // identity to the current auth.users row. Supabase sends a
      // confirmation email if the project requires it; we don't gate
      // on that here since the email step is opt-in.
      const { error } = await supabase.auth.updateUser({ email: trimmed, password })
      if (error) throw error
    },

    async unlinkProvider(provider: 'apple' | 'google' | 'email') {
      if (!configured) throw new Error('Supabase is not configured')
      const identities = session?.user?.identities ?? []
      const target = identities.find(i => i.provider === provider)
      if (!target) throw new Error(`No ${provider} identity to unlink`)
      if (identities.length <= 1) {
        throw new Error("Can't remove your only sign-in method. Link another one first.")
      }
      const { error } = await supabase.auth.unlinkIdentity(target)
      if (error) throw error
    },
  }), [session, profile, loading, configured, isGuest, inPasswordRecovery])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
